import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertTriangle, Trash2, Clock, CheckCircle, XCircle, 
  Play, StopCircle, Calendar, User, ShieldCheck, CalendarClock, FileDown, Paperclip
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DeadlineLight } from '@/components/DeadlineLight';
import { ComplianceTrendWidget } from '@/components/ComplianceTrendWidget';
import { RTBFAttachmentsDialog } from '@/components/RTBFAttachmentsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RTBFPage = () => {
  const { user } = useOutletContext();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, request: null, action: null });
  const [extendDialog, setExtendDialog] = useState({ open: false, request: null, months: '2', justification: '' });
  const [attachmentsDialog, setAttachmentsDialog] = useState({ open: false, request: null });
  const now = new Date();
  const [reportYear, setReportYear] = useState(String(now.getFullYear()));
  const [reportMonth, setReportMonth] = useState(String(now.getMonth() + 1));
  const [downloadingReport, setDownloadingReport] = useState(false);

  const canDeleteAttachment = ['dpo', 'admin', 'super_admin', 'tenant_admin'].includes(user?.role);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/rtbf/requests`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching RTBF requests:', error);
      toast.error('Erro ao carregar pedidos RTBF');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (rtbfId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/rtbf/${rtbfId}/execute`, {}, { withCredentials: true });
      toast.success('Direito ao Esquecimento executado com sucesso');
      fetchRequests();
    } catch (error) {
      console.error('Error executing RTBF:', error);
      toast.error(error.response?.data?.detail || 'Erro ao executar RTBF');
    }
    setConfirmDialog({ open: false, request: null, action: null });
  };

  const handleCancel = async (rtbfId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/rtbf/${rtbfId}`, { withCredentials: true });
      toast.success('Pedido RTBF cancelado');
      fetchRequests();
    } catch (error) {
      console.error('Error cancelling RTBF:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cancelar RTBF');
    }
    setConfirmDialog({ open: false, request: null, action: null });
  };

  const handleExtend = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/rtbf/${extendDialog.request?.rtbf_id}/extend-deadline`,
        { extension_months: Number(extendDialog.months), justification: extendDialog.justification },
        { withCredentials: true }
      );
      toast.success('Prazo prorrogado e justificação registada');
      setExtendDialog({ open: false, request: null, months: '2', justification: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao prorrogar o prazo');
    }
  };

  const downloadComplianceReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/rtbf/compliance-report/pdf?year=${reportYear}&month=${reportMonth}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `prova-cumprimento-rtbf-${reportYear}-${String(reportMonth).padStart(2, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Prova de Cumprimento descarregada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar o relatório');
    } finally {
      setDownloadingReport(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200">
            <Clock className="w-3 h-3 mr-1" />
            Agendado
          </Badge>
        );
      case 'pending_execution':
        return (
          <Badge className="bg-blue-50 text-blue-700 border-blue-200">
            <Play className="w-3 h-3 mr-1" />
            Pendente
          </Badge>
        );
      case 'executed':
        return (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Executado
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-slate-50 text-slate-700 border-slate-200">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelado
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-PT');
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrador',
      dpo: 'DPO',
      operador: 'Operador',
      titular: 'Titular'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  const scheduledRequests = requests.filter(r => r.status === 'scheduled' || r.status === 'pending_execution');
  const executedRequests = requests.filter(r => r.status === 'executed');
  const cancelledRequests = requests.filter(r => r.status === 'cancelled');

  return (
    <div className="space-y-6" data-testid="rtbf-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Direito ao Esquecimento
          </h1>
          <p className="text-slate-600">
            Gestão de pedidos RTBF - Artigo 17º do RGPD
          </p>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-[#0F172A]">{requests.length}</div>
            <p className="text-sm text-slate-600">Total Pedidos</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{scheduledRequests.length}</div>
            <p className="text-sm text-amber-700">Agendados</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">{executedRequests.length}</div>
            <p className="text-sm text-emerald-700">Executados</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-slate-600">{cancelledRequests.length}</div>
            <p className="text-sm text-slate-700">Cancelados</p>
          </CardContent>
        </Card>
      </div>

      {/* Compliance trend (last 12 months) */}
      <ComplianceTrendWidget />

      {/* Prova de Cumprimento (CNPD) */}
      <Card className="border-emerald-200" data-testid="compliance-report-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-700">
            <FileDown className="w-5 h-5" />
            Prova de Cumprimento (CNPD)
          </CardTitle>
          <CardDescription>
            Relatório mensal com prazos cumpridos e prorrogações justificadas (Art. 12.º(3) e 17.º RGPD) — pronto para fiscalização.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Ano</Label>
              <Select value={reportYear} onValueChange={setReportYear}>
                <SelectTrigger className="w-28" data-testid="report-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Mês</Label>
              <Select value={reportMonth} onValueChange={setReportMonth}>
                <SelectTrigger className="w-40" data-testid="report-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'].map((m, i) => (
                    <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={downloadComplianceReport}
              disabled={downloadingReport}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="download-compliance-report-button"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {downloadingReport ? 'A gerar...' : 'Descarregar prova em PDF'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending Requests */}
      {scheduledRequests.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Pedidos Pendentes ({scheduledRequests.length})
            </CardTitle>
            <CardDescription>
              Pedidos RTBF aguardando execução ou data de expiração
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titular</TableHead>
                  <TableHead>Solicitado Por</TableHead>
                  <TableHead>Data Execução</TableHead>
                  <TableHead>Prazo legal (RGPD)</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scheduledRequests.map((request) => (
                  <TableRow key={request.rtbf_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{request.user_name}</div>
                        <div className="text-xs text-slate-500">{request.user_email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-sm">{getRoleLabel(request.requested_by_role)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{formatDate(request.execution_date || request.scheduled_date)}</span>
                      </div>
                    </TableCell>
                    <TableCell><DeadlineLight request={request} /></TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">
                      {request.reason || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDialog({ open: true, request, action: 'execute' })}
                          className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                          data-testid={`execute-rtbf-${request.rtbf_id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Executar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDialog({ open: true, request, action: 'cancel' })}
                          data-testid={`cancel-rtbf-${request.rtbf_id}`}
                        >
                          <StopCircle className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                        {!request.extended && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setExtendDialog({ open: true, request, months: '2', justification: '' })}
                            data-testid={`extend-rtbf-${request.rtbf_id}`}
                          >
                            <CalendarClock className="w-4 h-4 mr-1" />
                            Prorrogar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAttachmentsDialog({ open: true, request })}
                          data-testid={`attachments-rtbf-${request.rtbf_id}`}
                        >
                          <Paperclip className="w-4 h-4 mr-1" />
                          Anexos{Array.isArray(request.attachments) && request.attachments.length > 0 ? ` (${request.attachments.length})` : ''}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Executed Requests */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Histórico de Pedidos RTBF
          </CardTitle>
          <CardDescription>
            Registo completo de pedidos de Direito ao Esquecimento
          </CardDescription>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Trash2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhum pedido RTBF registado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Titular</TableHead>
                  <TableHead>Solicitado Por</TableHead>
                  <TableHead>Data Criação</TableHead>
                  <TableHead>Data Execução</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Identidade Verificada</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.rtbf_id}>
                    <TableCell className="font-mono text-xs">{request.rtbf_id}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{request.user_name}</div>
                        <div className="text-xs text-slate-500">{request.user_email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {getRoleLabel(request.requested_by_role)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(request.requested_at || request.created_at)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {request.executed_at 
                        ? new Date(request.executed_at).toLocaleDateString('pt-PT')
                        : formatDate(request.execution_date || request.scheduled_date)
                      }
                    </TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell>
                      {request.identity_verified ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, request: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {confirmDialog.action === 'execute' ? 'Confirmar Execução' : 'Confirmar Cancelamento'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.action === 'execute' 
                ? `Tem a certeza que deseja executar o Direito ao Esquecimento para ${confirmDialog.request?.user_name}? Esta ação é irreversível.`
                : `Tem a certeza que deseja cancelar o pedido RTBF para ${confirmDialog.request?.user_name}?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, request: null, action: null })}>
              Voltar
            </Button>
            {confirmDialog.action === 'execute' ? (
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={() => handleExecute(confirmDialog.request?.rtbf_id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Executar Agora
              </Button>
            ) : (
              <Button 
                variant="outline"
                onClick={() => handleCancel(confirmDialog.request?.rtbf_id)}
              >
                <StopCircle className="w-4 h-4 mr-2" />
                Cancelar Pedido
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={extendDialog.open}
        onOpenChange={(open) => setExtendDialog({ ...extendDialog, open })}
      >
        <DialogContent data-testid="extend-deadline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5" /> Prorrogar prazo de resposta
            </DialogTitle>
            <DialogDescription>
              O RGPD (art. 12(3)) permite prorrogar o prazo de 1 mês por até 2 meses adicionais em pedidos
              complexos, desde que o titular seja informado e a decisão fique justificada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Prorrogação</Label>
              <Select
                value={extendDialog.months}
                onValueChange={(v) => setExtendDialog({ ...extendDialog, months: v })}
              >
                <SelectTrigger data-testid="extension-months-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">+1 mês (total 2 meses)</SelectItem>
                  <SelectItem value="2">+2 meses (total 3 meses - máximo legal)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Justificação (mín. 20 caracteres)</Label>
              <Textarea
                rows={4}
                placeholder="Ex.: pedido abrange 6 sistemas e requer validação junto de dois subcontratantes..."
                value={extendDialog.justification}
                onChange={(e) => setExtendDialog({ ...extendDialog, justification: e.target.value })}
                data-testid="extension-justification-input"
              />
              <p className="text-xs text-slate-500">
                Fica registada no histórico de auditoria — é o que a CNPD pede numa fiscalização.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExtendDialog({ open: false, request: null, months: '2', justification: '' })}
              data-testid="cancel-extension-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleExtend}
              disabled={extendDialog.justification.trim().length < 20}
              data-testid="confirm-extension-button"
            >
              <CalendarClock className="w-4 h-4 mr-2" /> Confirmar prorrogação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RTBFAttachmentsDialog
        open={attachmentsDialog.open}
        onOpenChange={(open) => setAttachmentsDialog({ open, request: attachmentsDialog.request })}
        request={attachmentsDialog.request}
        canDelete={canDeleteAttachment}
      />
    </div>
  );
};

export default RTBFPage;
